import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';
import type { AdminUser, CommitmentPreview, Project, WorkItemType } from '../lib/types';
import { formatDate, relativeDays, toDateTimeInput } from '../lib/format';
import {
  Badge,
  Button,
  Callout,
  Checkbox,
  Dialog,
  Field,
  Input,
  Select,
  Textarea,
  useToast,
} from '../components/ui';

/**
 * Creating work.
 *
 * The commitment path is the interesting one. As soon as a customer date is
 * entered, the form asks the server for §9's suggested internal dates and §35's
 * feasibility verdict, and shows both *before* the CSM commits. That ordering is
 * the whole design intent of §35: warn while the decision is still being made,
 * then record whatever is actually true.
 */

type CreatableType = Extract<
  WorkItemType,
  'task' | 'customer_commitment' | 'customer_followup' | 'opportunity_signal'
>;

const TYPE_OPTIONS: Array<{ value: CreatableType; label: string; help: string }> = [
  {
    value: 'customer_commitment',
    label: 'Customer commitment',
    help: 'Something we owe the customer by a date. Carries a customer date, an internal target and a readiness check.',
  },
  {
    value: 'customer_followup',
    label: 'Waiting on customer',
    help: 'Something the customer owes us. Surfaces when the follow-up date arrives, or earlier if they reply.',
  },
  {
    value: 'task',
    label: 'Task',
    help: 'Internal work with no external promise attached.',
  },
  {
    value: 'opportunity_signal',
    label: 'Opportunity signal',
    help: 'An expansion signal worth acting on. Not a CRM opportunity.',
  },
];

export function CreateWorkItemDialog({
  accountId,
  accountName,
  defaultType = 'customer_commitment',
  onClose,
  onCreated,
}: {
  accountId: string;
  accountName: string;
  defaultType?: CreatableType;
  onClose: () => void;
  onCreated?: (workItemId: string) => void;
}) {
  const { me } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [type, setType] = useState<CreatableType>(defaultType);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ownerUserId, setOwnerUserId] = useState(me.id);
  const [priority, setPriority] = useState('medium');
  const [projectId, setProjectId] = useState('');

  // Commitment fields
  const [customerDueAt, setCustomerDueAt] = useState('');
  const [category, setCategory] = useState('delivery');
  const [externallyCommitted, setExternallyCommitted] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideInternalDates, setOverrideInternalDates] = useState(false);
  const [internalTargetAt, setInternalTargetAt] = useState('');
  const [internalCheckAt, setInternalCheckAt] = useState('');

  // Follow-up fields
  const [waitingFor, setWaitingFor] = useState('');
  const [followupAt, setFollowupAt] = useState('');
  const [customerExpectedAt, setCustomerExpectedAt] = useState('');

  // Opportunity fields
  const [signalType, setSignalType] = useState('general_expansion');
  const [potentialValue, setPotentialValue] = useState('');
  const [targetPeriod, setTargetPeriod] = useState('');

  const [dueAt, setDueAt] = useState('');

  const users = useQuery({
    queryKey: ['admin-users-lite'],
    queryFn: () => api.get<{ data: AdminUser[] }>('/admin/users'),
    // Non-admins cannot list users; the owner select then just offers the caller.
    retry: false,
  });

  const projects = useQuery({
    queryKey: ['projects', accountId],
    queryFn: () => api.get<{ data: Project[] }>('/projects', { accountId, openOnly: true, limit: 100 }),
  });

  /**
   * §9 + §35 preview. Debounced because it fires as the date field is typed
   * into, and re-run whenever anything the verdict depends on changes.
   */
  const [debouncedDate, setDebouncedDate] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedDate(customerDueAt), 350);
    return () => clearTimeout(timer);
  }, [customerDueAt]);

  const preview = useQuery({
    queryKey: ['commitment-preview', accountId, debouncedDate, externallyCommitted, category],
    enabled: type === 'customer_commitment' && Boolean(debouncedDate),
    queryFn: () =>
      api.post<CommitmentPreview>('/work-items/commitments/preview', {
        accountId,
        customerDueAt: new Date(debouncedDate).toISOString(),
        externallyCommitted,
        commitmentCategory: category,
      }),
  });

  // Adopt the suggestion unless the CSM has taken the dates over.
  useEffect(() => {
    if (!preview.data || overrideInternalDates) return;
    setInternalTargetAt(toDateTimeInput(preview.data.suggestedDates.internalTargetAt));
    setInternalCheckAt(toDateTimeInput(preview.data.suggestedDates.internalCheckAt));
  }, [preview.data, overrideInternalDates]);

  const gate = preview.data?.promiseGovernance;
  const needsOverrideReason = Boolean(gate?.requiresOverrideReason);

  const mutation = useMutation({
    mutationFn: () => {
      const base = {
        accountId,
        title,
        description: description || null,
        ownerUserId,
        priority,
        projectId: projectId || null,
      };

      if (type === 'customer_commitment') {
        return api.post<{ workItem: { workItem: { id: string } } }>('/work-items', {
          ...base,
          type,
          commitment: {
            customerDueAt: new Date(customerDueAt).toISOString(),
            internalTargetAt: overrideInternalDates && internalTargetAt
              ? new Date(internalTargetAt).toISOString()
              : undefined,
            internalCheckAt: overrideInternalDates && internalCheckAt
              ? new Date(internalCheckAt).toISOString()
              : undefined,
            commitmentCategory: category,
            externallyCommitted,
            overrideWarningReason: overrideReason || null,
          },
        });
      }
      if (type === 'customer_followup') {
        return api.post<{ workItem: { workItem: { id: string } } }>('/work-items', {
          ...base,
          type,
          followup: {
            waitingFor,
            followupAt: new Date(followupAt).toISOString(),
            customerExpectedAt: customerExpectedAt
              ? new Date(customerExpectedAt).toISOString()
              : null,
          },
        });
      }
      if (type === 'opportunity_signal') {
        return api.post<{ workItem: { workItem: { id: string } } }>('/work-items', {
          ...base,
          type,
          opportunity: {
            signalType,
            potentialValueText: potentialValue || null,
            targetPeriod: targetPeriod || null,
          },
          dueAt: dueAt ? new Date(dueAt).toISOString() : null,
        });
      }
      return api.post<{ workItem: { workItem: { id: string } } }>('/work-items', {
        ...base,
        type: 'task',
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
      });
    },
    onSuccess: (response) => {
      toast(`${TYPE_OPTIONS.find((option) => option.value === type)?.label} recorded`);
      void queryClient.invalidateQueries({ queryKey: ['my-work'] });
      void queryClient.invalidateQueries({ queryKey: ['work-items'] });
      void queryClient.invalidateQueries({ queryKey: ['account-summary', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['leadership'] });
      onCreated?.(response.workItem.workItem.id);
      onClose();
    },
  });

  const ownerOptions = useMemo(() => {
    const list = users.data?.data ?? [];
    if (list.length > 0) return list.map((u) => ({ id: u.id, name: u.fullName }));
    return [{ id: me.id, name: me.fullName }];
  }, [users.data, me]);

  const canSubmit = (() => {
    if (!title.trim()) return false;
    if (type === 'customer_commitment') {
      if (!customerDueAt) return false;
      if (needsOverrideReason && !overrideReason.trim()) return false;
    }
    if (type === 'customer_followup') return Boolean(waitingFor.trim() && followupAt);
    return true;
  })();

  const selectedType = TYPE_OPTIONS.find((option) => option.value === type)!;

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title="Record work"
      description={accountName}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving…' : 'Record'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {TYPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setType(option.value)}
                className={
                  'rounded-md border px-2.5 py-2 text-left text-xs font-medium transition-colors ' +
                  (type === option.value
                    ? 'border-accent bg-accent-soft text-ink'
                    : 'border-border text-ink-2 hover:border-border-strong hover:text-ink')
                }
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-muted">{selectedType.help}</p>
        </div>

        <Field label="Title" required>
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={
              type === 'customer_commitment'
                ? 'e.g. Send the revised certification report'
                : type === 'customer_followup'
                  ? 'e.g. Zone-level rep list for wave 2'
                  : 'What needs doing?'
            }
          />
        </Field>

        <Field label="Detail" hint="Optional context. Shown on the work item.">
          <Textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={2}
          />
        </Field>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Owner">
            <Select value={ownerUserId} onChange={(event) => setOwnerUserId(event.target.value)}>
              {ownerOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={priority} onChange={(event) => setPriority(event.target.value)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </Select>
          </Field>
          <Field label="Project" hint="Optional">
            <Select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
              <option value="">Not linked to a project</option>
              {(projects.data?.data ?? []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {type === 'customer_commitment' && (
          <div className="space-y-3 rounded-lg border border-border bg-surface-2/50 p-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Customer due date" required hint="The date the customer is working to">
                <Input
                  type="datetime-local"
                  value={customerDueAt}
                  onChange={(event) => setCustomerDueAt(event.target.value)}
                />
              </Field>
              <Field label="Category">
                <Select value={category} onChange={(event) => setCategory(event.target.value)}>
                  <option value="delivery">Delivery</option>
                  <option value="product_enhancement">Product enhancement</option>
                  <option value="report">Report</option>
                  <option value="configuration">Configuration</option>
                  <option value="response">Response</option>
                  <option value="meeting_action">Meeting action</option>
                  <option value="custom">Other</option>
                </Select>
              </Field>
            </div>

            <Checkbox
              label="This date has been communicated to the customer"
              hint="Leave unchecked to record a requested date that has not been promised. This distinction is what stops accidental overpromising."
              checked={externallyCommitted}
              onChange={(event) => setExternallyCommitted(event.target.checked)}
            />

            {/* §9 — the suggested internal dates, with the rule that produced them */}
            {preview.data && (
              <div className="rounded-md border border-border bg-surface p-2.5">
                <p className="mb-2 text-xs font-semibold text-ink">
                  Suggested internal dates
                  <span className="ml-2 font-normal text-muted">
                    {preview.data.calendarTimezone} business calendar
                  </span>
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div>
                    <p className="text-xs text-muted">Internal target</p>
                    <p className="text-sm text-ink">
                      {formatDate(preview.data.suggestedDates.internalTargetAt)}
                      <span className="ml-1.5 text-xs text-muted">
                        {relativeDays(preview.data.suggestedDates.internalTargetAt)}
                      </span>
                    </p>
                    <p className="mt-0.5 text-[11px] text-muted">
                      {preview.data.suggestedDates.targetRule}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted">Readiness check</p>
                    <p className="text-sm text-ink">
                      {formatDate(preview.data.suggestedDates.internalCheckAt)}
                      <span className="ml-1.5 text-xs text-muted">
                        {relativeDays(preview.data.suggestedDates.internalCheckAt)}
                      </span>
                    </p>
                    <p className="mt-0.5 text-[11px] text-muted">
                      {preview.data.suggestedDates.checkRule}
                    </p>
                  </div>
                </div>
                <div className="mt-2">
                  <Checkbox
                    label="Set the internal dates myself"
                    checked={overrideInternalDates}
                    onChange={(event) => setOverrideInternalDates(event.target.checked)}
                  />
                </div>
                {overrideInternalDates && (
                  <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="Internal target">
                      <Input
                        type="datetime-local"
                        value={internalTargetAt}
                        onChange={(event) => setInternalTargetAt(event.target.value)}
                      />
                    </Field>
                    <Field label="Readiness check">
                      <Input
                        type="datetime-local"
                        value={internalCheckAt}
                        onChange={(event) => setInternalCheckAt(event.target.value)}
                      />
                    </Field>
                  </div>
                )}
              </div>
            )}

            {/* §35 promise governance */}
            {gate?.applicable && !gate.feasibilityConfirmed && (
              <Callout tone="warning" title="Product has not confirmed this timeline">
                <p>{gate.warning}</p>
                {gate.findings.length > 0 && (
                  <ul className="mt-1.5 list-disc space-y-0.5 pl-4">
                    {gate.findings.map((finding) => (
                      <li key={finding}>{finding}</li>
                    ))}
                  </ul>
                )}
                {gate.recommendation === 'record_requested_date_only' && (
                  <p className="mt-1.5 font-medium text-ink">
                    Nothing has been promised yet — leave the box above unchecked and this stays a
                    requested date rather than a commitment.
                  </p>
                )}
              </Callout>
            )}

            {gate?.applicable && gate.feasibilityConfirmed && (
              <Callout tone="good" title="Product's dates support this commitment">
                Status, target date, delivery confidence and the engineering estimate all line up.
              </Callout>
            )}

            {needsOverrideReason && (
              <Field
                label="Why is this being recorded as a commitment?"
                required
                hint="Recording an already-made promise is always allowed. The reason is stored with it and audited."
              >
                <Textarea
                  value={overrideReason}
                  onChange={(event) => setOverrideReason(event.target.value)}
                  rows={2}
                  placeholder="e.g. Already promised on the escalation call before Product confirmed feasibility."
                />
              </Field>
            )}
          </div>
        )}

        {type === 'customer_followup' && (
          <div className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-surface-2/50 p-3 sm:grid-cols-3">
            <Field label="What are we waiting for?" required>
              <Input
                value={waitingFor}
                onChange={(event) => setWaitingFor(event.target.value)}
                placeholder="e.g. Confirmed rep list from Sneha"
              />
            </Field>
            <Field label="Customer expected by" hint="What they said">
              <Input
                type="datetime-local"
                value={customerExpectedAt}
                onChange={(event) => setCustomerExpectedAt(event.target.value)}
              />
            </Field>
            <Field label="Follow up on" required hint="When to chase if nothing arrives">
              <Input
                type="datetime-local"
                value={followupAt}
                onChange={(event) => setFollowupAt(event.target.value)}
              />
            </Field>
          </div>
        )}

        {type === 'opportunity_signal' && (
          <div className="grid grid-cols-1 gap-3 rounded-lg border border-border bg-surface-2/50 p-3 sm:grid-cols-3">
            <Field label="Signal">
              <Select value={signalType} onChange={(event) => setSignalType(event.target.value)}>
                <option value="additional_country">Additional country</option>
                <option value="additional_division">Additional division</option>
                <option value="additional_use_case">Additional use case</option>
                <option value="additional_module">Additional module</option>
                <option value="license_growth">Licence growth</option>
                <option value="general_expansion">General expansion</option>
              </Select>
            </Field>
            <Field label="Potential" hint="Free text; not a forecast">
              <Input
                value={potentialValue}
                onChange={(event) => setPotentialValue(event.target.value)}
                placeholder="e.g. ~180 additional licences"
              />
            </Field>
            <Field label="Target period">
              <Input
                value={targetPeriod}
                onChange={(event) => setTargetPeriod(event.target.value)}
                placeholder="e.g. Next financial year"
              />
            </Field>
          </div>
        )}

        {(type === 'task' || type === 'opportunity_signal') && (
          <Field label="Due date" hint="Optional">
            <Input
              type="datetime-local"
              value={dueAt}
              onChange={(event) => setDueAt(event.target.value)}
            />
          </Field>
        )}

        {mutation.error instanceof ApiError && (
          <Callout tone="critical" title={mutation.error.message}>
            {mutation.error.requiresOverrideReason && (
              <p>Add a reason above and record it again.</p>
            )}
            {mutation.error.findings.length > 0 && (
              <ul className="mt-1 list-disc space-y-0.5 pl-4">
                {mutation.error.findings.map((finding) => (
                  <li key={finding}>{finding}</li>
                ))}
              </ul>
            )}
          </Callout>
        )}

        {type === 'customer_commitment' && !customerDueAt && (
          <p className="text-xs text-muted">
            Enter a customer date to see the suggested internal dates and whether Product has
            confirmed the timeline.
          </p>
        )}
      </div>
    </Dialog>
  );
}

export { Badge };
